import { Button, ColorPicker, Form, Input, message, Popconfirm, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import './ModalEditState.scss';
import { useTranslation } from '../../i18n/i18n';
import { EditOutlined } from '@ant-design/icons';
import { InstallationState, InstallationStateType } from '../../gen/proto/api/gateway/v1/installation_state_pb';
import { installationStateApi } from '../../api/installation-state.api';
import ModalCustom from '../utils/ModalCustom/ModalCustom';
import useAuthStore from '../../stores/auth.store';

const { Text } = Typography;

interface ModalEditStateProps {
  fullStates: InstallationState[];
  onSaved?: () => void;
}

const ModalEditState = ({ fullStates, onSaved }: ModalEditStateProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { t } = useTranslation('location');
  const [messageApi, contextHolder] = message.useMessage();
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);

  const colorTranslations = useMemo<Record<InstallationStateType, string | undefined>>(
    () => ({
      [InstallationStateType.OK]: t('OK'),
      [InstallationStateType.URGENT]: t('urgent'),
      [InstallationStateType.PUBLIC_WATER_INVASION]: t('invasion'),
      [InstallationStateType.IN_MAINTENANCE]: t('maintenance'),
      [InstallationStateType.ERROR]: t('error'),
      [InstallationStateType.CUSTOM]: undefined,
      [InstallationStateType.UNSPECIFIED]: undefined,
    }),
    [t],
  );

  const [installationState, setInstallationState] = useState<InstallationState>(fullStates[0]);
  const [selectedColor, setSelectedColor] = useState<string>(fullStates[0]?.color ?? '');
  const [selectedName, setSelectedName] = useState<string>(
    colorTranslations[fullStates[0]?.state] ?? fullStates[0]?.customName ?? '',
  );

  useEffect(() => {
    if (fullStates[0]) {
      const name = colorTranslations[fullStates[0].state] ?? fullStates[0].customName ?? '';
      setInstallationState(fullStates[0]);
      setSelectedColor(fullStates[0].color ?? '');
      setSelectedName(name);
    }
  }, [fullStates, colorTranslations]);

  const deleteState = async () => {
    try {
      await installationStateApi.deleteInstallationState(installationState.uuid);
      onSaved?.();
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setIsModalOpen(false);
    }
  };

  const editState = async () => {
    try {
      await form.validateFields();
      await installationStateApi.updateInstallationState(installationState.uuid, selectedName, selectedColor);
      onSaved?.();
      setIsModalOpen(false);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  const handleSelectState = (state: InstallationState) => {
    setInstallationState(state);
    setSelectedColor(state.color);
    const name = colorTranslations[state.state] ?? state.customName ?? '';
    setSelectedName(name);
    form.setFieldValue('name', name);
  };
  return (
    <>
      {contextHolder}
      <Button
        type={'default'}
        onClick={() => {
          form.setFieldValue('name', selectedName);
          setIsModalOpen(true);
        }}
      >
        <EditOutlined />
        {t('modals.editState')}
      </Button>
      <ModalCustom
        title={t('modals.editState')}
        description={t('modals.modalDesc')}
        footer={null}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      >
        <div className={'ModalEditState__modal'}>
          <div className={'ModalEditState__content'}>
            <Text className={'ModalEditState__content__title'}>{t('modals.states')}</Text>
            {fullStates.map((state) => (
              <div
                key={state.uuid}
                className={`ModalEditState__stateItem${installationState?.uuid === state.uuid ? ' ModalEditState__stateItem--selected' : ''}`}
                onClick={() => handleSelectState(state)}
              >
                <div style={{ backgroundColor: state.color }} className={'ModalEditState__stateItem__color'} />
                <Text>{colorTranslations[state.state] ?? state.customName}</Text>
              </div>
            ))}
          </div>
          <div className={'ModalEditState__content__editArea'}>
            <div className={'ModalEditState__content__editArea__topArea'}>
              <div
                className={'ModalEditState__content__editArea__color'}
                style={{ backgroundColor: installationState?.color }}
              />
              {installationState && (
                <Text>{colorTranslations[installationState.state] ?? installationState.customName}</Text>
              )}
            </div>
            {installationState && (
              <Form form={form} layout={'vertical'} className={'ModalEditState__form'}>
                <Form.Item label={t('modals.name')} name={'name'}>
                  <Input
                    disabled={installationState.state !== InstallationStateType.CUSTOM}
                    onChange={(e) => setSelectedName(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label={t('modals.color')}>
                  <ColorPicker
                    value={selectedColor}
                    showText
                    disabledAlpha
                    disabledFormat
                    onChange={(value) => setSelectedColor(value.toHexString())}
                  />
                </Form.Item>
              </Form>
            )}
            <div className={'ModalEditState__content__editArea__buttons'}>
              {isSuperAdmin && (
                <Popconfirm
                  title={t('modals.confirmDelete')}
                  description={t('modals.confirmDesc', {
                    name: colorTranslations[installationState?.state] ?? installationState?.customName ?? '',
                  })}
                  okText={t('modals.yes')}
                  okButtonProps={{ danger: true }}
                  cancelText={t('modals.no')}
                  onConfirm={deleteState}
                >
                  <Button type={'primary'} danger>
                    {t('modals.deleteState')}
                  </Button>
                </Popconfirm>
              )}
              <Button type={'primary'} onClick={editState}>
                {t('modals.save')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCustom>
    </>
  );
};

export default ModalEditState;
